import type { ClassId } from "./classes.ts";
import { SKILL_BY_ID, type SkillDef } from "./skills.ts";

/**
 * Juramento da Alvorada (F70, doc 09 §4, item 11): a cada amanhecer e a cada chefe derrubado, cada
 * jogador escolhe 1 entre 2 melhorias da própria classe, até 5 por partida. Cada uma soma num número da
 * habilidade (`SkillDef.params`, ou a recarga em `cooldownMs`), só no servidor: nada do que o simulador
 * prevê (a distância e a recarga da Investida, a velocidade do Salto) muda.
 */
export interface OathDef {
  readonly id: string;
  readonly classId: ClassId;
  readonly name: string;
  readonly description: string;
  /** Habilidade e número que muda (`cooldownMs` é a recarga). */
  readonly skill: string;
  readonly param: string;
  readonly add: number;
}

/** Quantos juramentos cada jogador faz por partida. */
export const OATH_SLOTS = 5;

const oath = (classId: ClassId, id: string, name: string, description: string, skill: string, param: string, add: number): OathDef => ({ id, classId, name, description, skill, param, add });

export const OATHS: readonly OathDef[] = [
  oath("baluarte", "brado-amplo", "Brado Amplo", "Brado alcança +2 m.", "brado", "radius", 2),
  oath("baluarte", "brado-firme", "Brado Firme", "Provocação dura +1,5 s (fora os chefes).", "brado", "tauntMs", 1500),
  oath("baluarte", "brado-rapido", "Brado Rápido", "Brado recarrega 2 s mais rápido.", "brado", "cooldownMs", -2000),
  oath("baluarte", "bastiao-longo", "Bastião Longo", "Bastião dura +2 s.", "bastiao", "durationMs", 2000),
  oath("baluarte", "bastiao-amplo", "Bastião Amplo", "Cúpula +1,5 m.", "bastiao", "radius", 1.5),
  oath("lanceiro", "investida-feroz", "Investida Feroz", "Investida causa +12 de dano.", "investida", "damage", 12),
  oath("lanceiro", "investida-segura", "Investida Segura", "+0,2 s invulnerável na Investida.", "investida", "invulnMs", 200),
  oath("lanceiro", "investida-larga", "Investida Larga", "A faixa da Investida fica 0,8 m mais larga.", "investida", "width", 0.8),
  oath("lanceiro", "salto-pesado", "Salto Pesado", "Pouso do Salto causa +20 de dano.", "salto-do-falcao", "damage", 20),
  oath("lanceiro", "salto-amplo", "Salto Amplo", "Pouso do Salto alcança +1,5 m.", "salto-do-falcao", "radius", 1.5),
  oath("arcanista", "runa-ampla", "Runa Ampla", "Runa +1,5 m.", "runa-de-contencao", "radius", 1.5),
  oath("arcanista", "runa-longa", "Runa Longa", "Prende +0,7 s.", "runa-de-contencao", "rootMs", 700),
  oath("arcanista", "runa-rapida", "Runa Rápida", "Runa recarrega 2,5 s mais rápido.", "runa-de-contencao", "cooldownMs", -2500),
  oath("arcanista", "fenix-longa", "Fênix Longa", "Círculo da Fênix dura +2 s.", "circulo-da-fenix", "durationMs", 2000),
  oath("arcanista", "fenix-forte", "Fênix Forte", "Círculo cura +2 de Vida por segundo.", "circulo-da-fenix", "heal", 2),
  oath("patrulheiro", "sinal-amplo", "Sinal Amplo", "Flecha Sinalizadora marca +3 m.", "flecha-sinalizadora", "radius", 3),
  oath("patrulheiro", "sinal-longo", "Sinal Longo", "Marca +3 s.", "flecha-sinalizadora", "markMs", 3000),
  oath("patrulheiro", "sinal-rapido", "Sinal Rápido", "Sinalizadora recarrega 3 s mais rápido.", "flecha-sinalizadora", "cooldownMs", -3000),
  oath("patrulheiro", "rompe-forte", "Rompe Forte", "Rompe-Núcleo causa +25 de dano.", "disparo-rompe-nucleo", "damage", 25),
  oath("patrulheiro", "rompe-longo", "Rompe Longo", "Exposto dura +2 s.", "disparo-rompe-nucleo", "exposeMs", 2000),
];

export const OATH_BY_ID: ReadonlyMap<string, OathDef> = new Map(OATHS.map((o) => [o.id, o]));

/** Os juramentos da classe que ainda não foram feitos. */
export const oathsLeft = (classId: ClassId, taken: readonly string[]): OathDef[] => OATHS.filter((o) => o.classId === classId && !taken.includes(o.id));

/** Duas opções (ou menos, no fim) para quem ainda tem vaga, sorteadas por `random` (0 ≤ r < 1). */
export function oathOffer(classId: ClassId, taken: readonly string[], random: () => number): string[] {
  if (taken.length >= OATH_SLOTS) return [];
  const pool = oathsLeft(classId, taken).map((o) => o.id);
  const out: string[] = [];
  while (out.length < 2 && pool.length > 0) out.push(pool.splice(Math.floor(random() * pool.length), 1)[0]!);
  return out;
}

/** Um número da habilidade com os juramentos (a recarga em `cooldownMs`, nunca abaixo de 1 s). */
export function oathParam(skill: SkillDef, key: string, taken: readonly string[]): number {
  const base = key === "cooldownMs" ? (skill.cooldownMs ?? 0) : (skill.params?.[key] ?? 0);
  let v = base;
  for (const id of taken) {
    const o = OATH_BY_ID.get(id);
    if (o && o.skill === skill.id && o.param === key) v += o.add;
  }
  return key === "cooldownMs" && base > 0 ? Math.max(1000, v) : v;
}

/** Os juramentos guardados como texto público (`PlayerInfo.oaths`, separados por vírgula). */
export const parseOaths = (text: string): string[] => (text ? text.split(",").filter((id) => OATH_BY_ID.has(id)) : []);

/** Confere a coerência dos dados: cada juramento muda um número que existe na habilidade da classe. */
export function oathTargetExists(o: OathDef): boolean {
  const s = SKILL_BY_ID.get(o.skill);
  return s !== undefined && (o.param === "cooldownMs" ? s.cooldownMs !== undefined : s.params?.[o.param] !== undefined);
}
